import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import purchaseReportService from '../../../../services/purchaseReportService';
import PriceTrackingShell from './PriceTrackingShell';
import usePriceTrackingFilters from './usePriceTrackingFilters';
import { formatDateLocaleLima, formatMoney, formatPrice, formatPct } from './shared';

export default function PriceTrackingProductDetailPage() {
  const { productId } = useParams();
  const { filters, updateFilter, buildParams, suppliers, products } = usePriceTrackingFilters();
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const loadData = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const res = await purchaseReportService.getPriceTrackingProduct(productId, buildParams());
      if (res.success) {
        setDetail(res.data);
      } else {
        setToast({ isOpen: true, message: res.message || 'Sin datos', type: 'warning' });
      }
    } catch (error) {
      setToast({ isOpen: true, message: 'Error al cargar detalle', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [productId, buildParams]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const chartData = detail?.history?.map((h) => ({
    date: formatDateLocaleLima(h.issue_date),
    price: h.net_unit_price,
  })) || [];

  return (
    <PriceTrackingShell
      title="Detalle de insumo"
      subtitle="Evolución de precio, historial de compras y comparación por proveedor."
      filters={filters}
      updateFilter={updateFilter}
      suppliers={suppliers}
      products={products}
      onQuery={loadData}
      loading={loading}
      toast={toast}
      onToastClose={() => setToast({ ...toast, isOpen: false })}
    >
      <Link
        to="/compras/reports/price-tracking/products"
        className="inline-block text-sm text-teal-600 hover:text-teal-800 mb-4"
      >
        ← Volver al listado de insumos
      </Link>

      {loading && !detail ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center text-gray-500">Cargando...</div>
      ) : !detail ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center text-gray-500">Sin datos para este producto.</div>
      ) : (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex flex-wrap justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{detail.product?.name}</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {detail.product?.sku || 'Sin SKU'}
                  {detail.product?.is_insumo ? ' · Insumo' : ''}
                  {detail.product?.unit_of_measure ? ` · ${detail.product.unit_of_measure}` : ''}
                </p>
              </div>
              {detail.alert && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
                  <strong>Alerta:</strong> último precio {formatPct(detail.alert.variation_pct)} vs compra anterior
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
              {[
                ['Último precio', formatPrice(detail.summary?.last_price)],
                ['Promedio', formatPrice(detail.summary?.avg_price)],
                ['Mínimo', formatPrice(detail.summary?.min_price)],
                ['Máximo', formatPrice(detail.summary?.max_price)],
                ['Gasto total', formatMoney(detail.summary?.total_spend, detail.summary?.currency)],
              ].map(([label, value]) => (
                <div key={label} className="rounded-lg bg-gray-50 px-4 py-3">
                  <div className="text-xs text-gray-500 uppercase">{label}</div>
                  <div className="text-lg font-semibold mt-1">{value}</div>
                </div>
              ))}
            </div>
          </div>

          {chartData.length > 1 && (
            <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4">Evolución del precio neto</h3>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tickFormatter={formatPrice} />
                  <Tooltip formatter={(v) => formatPrice(v)} />
                  <Line type="monotone" dataKey="price" stroke="#0F766E" strokeWidth={2} dot={{ r: 4 }} name="Precio neto" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <h3 className="font-semibold px-5 py-4 border-b">Historial de compras</h3>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs text-gray-500">Fecha</th>
                    <th className="px-3 py-2 text-left text-xs text-gray-500">Serie</th>
                    <th className="px-3 py-2 text-left text-xs text-gray-500">Número</th>
                    <th className="px-3 py-2 text-left text-xs text-gray-500">Proveedor</th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500">Precio</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[...(detail.history || [])].reverse().map((h, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2">{formatDateLocaleLima(h.issue_date)}</td>
                      <td className="px-3 py-2">{h.series || '—'}</td>
                      <td className="px-3 py-2">{h.number || '—'}</td>
                      <td className="px-3 py-2">{h.supplier_name}</td>
                      <td className="px-3 py-2 text-right font-medium">{formatPrice(h.net_unit_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <h3 className="font-semibold px-5 py-4 border-b">Precio por proveedor</h3>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs text-gray-500">Proveedor</th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500">Prom.</th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500">Último</th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500">Gasto</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(detail.by_supplier || []).map((s) => (
                    <tr key={s.supplier_id}>
                      <td className="px-3 py-2">{s.supplier_name}</td>
                      <td className="px-3 py-2 text-right">{formatPrice(s.avg_price)}</td>
                      <td className="px-3 py-2 text-right font-medium">{formatPrice(s.last_price)}</td>
                      <td className="px-3 py-2 text-right">{formatMoney(s.total_spend)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </PriceTrackingShell>
  );
}
